import {EventParticipant, GroupAvatar, Participating, UserAvatar} from "@/lib/types";
import Page from "@/lib/pages/Page";
import ErrorPage from "@/lib/pages/ErrorPage";
import SpinnerPage from "@/lib/pages/SpinnerPage";
import Header from "@/lib/components/Header";
import ParticipantTable from "@/lib/components/ParticipantTable";
import Nav from "@/lib/components/Nav";
import NavButton from "@/lib/components/NavButton";
import {useState} from "react";
import {useEvent} from "@/lib/providers/EventProvider";
import AvatarModal from "@/lib/components/AvatarModal";
import useCrossTabNavigation from "@/lib/crosstab";

class Segmentation {
    attending: EventParticipant[];
    not_attending: EventParticipant[];
    following: EventParticipant[];
    invited: EventParticipant[];
    solicitors: EventParticipant[];
    blocked: EventParticipant[];

    constructor(participants: EventParticipant[]) {
        this.attending = participants.filter(p => (
            p.status === Participating.ATTENDING ||
            p.status === Participating.MODERATOR ||
            p.status === Participating.HOST
        ));
        this.not_attending = participants.filter(p => p.status === Participating.NOTATTENDING);
        this.following = participants.filter(p => p.status === Participating.FOLLOWING);
        this.invited = participants.filter(p => p.status === Participating.INVITED);
        this.solicitors = participants.filter(p => p.status === Participating.SOLICITOR);
        this.blocked = participants.filter(p => p.status === Participating.BLOCKED);
    }
}

export default function ParticipantsPage() {
    const {navigate} = useCrossTabNavigation();

    const [avatarPreview, setAvatarPreview] = useState<UserAvatar | GroupAvatar | null>(null);

    const {event, eventLoading, eventError} = useEvent();

    if (eventError) {
        return <ErrorPage error={eventError}/>
    } else if (eventLoading || !event) {
        return <SpinnerPage/>
    }

    const parts = new Segmentation(event.participants);

    const editCallback = (username: string) => {
        if (event.permissions.moderate) {
            return () => {
                navigate(`/e/${event.event_id}/participants/${username}`)
            };
        } else {
            return undefined;
        }
    }

    const avatarCallback = (avatar: UserAvatar | GroupAvatar) => {
        setAvatarPreview(avatar);
    };

    const clearAvatar = () => {
        setAvatarPreview(null);
    }

    const userAvatarPreview = avatarPreview as UserAvatar;
    //const groupAvatarPreview = avatarPreview as GroupAvatar;

    return (
        <Page scrollable={true}>
            {userAvatarPreview && <AvatarModal avatar={userAvatarPreview} visible={!!avatarPreview} onRequestClose={clearAvatar}/>}

            {event.permissions.invite &&
                <Nav vertical={true} style={{marginBottom: 8}}>
                    <NavButton title={"Inviter bekjente"}
                               icon={"relation"}
                               href={`/e/${event.event_id}/invite`}/>
                </Nav>
            }
            {parts.attending.length > 0 && (
                <>
                    <Header title={`Påmeldt ( ${parts.attending.length} )`}/>
                    <ParticipantTable participants={parts.attending}
                                      onEdit={editCallback}
                                      onPress={avatarCallback}
                                      color={event.color}
                                      contrastColor={event.contrast_color}
                                      colored={true}/>
                </>
            )}
            {parts.solicitors.length > 0 && (
                <>
                    <Header title={`Ber om invitasjon ( ${parts.solicitors.length} )`}/>
                    <ParticipantTable participants={parts.solicitors}
                                      onEdit={editCallback}
                                      onPress={avatarCallback}
                                      color={event.color}
                                      contrastColor={event.contrast_color}
                                      colored={false}/>
                </>
            )}
            {parts.following.length > 0 && (
                <>
                    <Header title={`Følger eventet ( ${parts.following.length} )`}/>
                    <ParticipantTable participants={parts.following}
                                      onEdit={editCallback}
                                      onPress={avatarCallback}
                                      color={event.color}
                                      contrastColor={event.contrast_color}
                                      colored={false}/>
                </>
            )}
            {parts.invited.length > 0 && (
                <>
                    <Header title={`Invitert ( ${parts.invited.length} )`}/>
                    <ParticipantTable participants={parts.invited}
                                      onEdit={editCallback}
                                      onPress={avatarCallback}
                                      color={event.color}
                                      contrastColor={event.contrast_color}
                                      colored={false}/>
                </>
            )}
            {parts.not_attending.length > 0 && (
                <>
                    <Header title={`Kommer ikke ( ${parts.not_attending.length} )`}/>
                    <ParticipantTable participants={parts.not_attending}
                                      onEdit={editCallback}
                                      onPress={avatarCallback}
                                      color={event.color}
                                      contrastColor={event.contrast_color}
                                      colored={false}/>
                </>
            )}
            {parts.blocked.length > 0 && (
                <>
                    <Header title={`Blokkert ( ${parts.blocked.length} )`}/>
                    <ParticipantTable participants={parts.blocked}
                                      onEdit={editCallback}
                                      onPress={avatarCallback}
                                      color={event.color}
                                      contrastColor={event.contrast_color}
                                      colored={false}/>
                </>
            )}
        </Page>
    )
}